import React, { useState } from "react";
import {
  Box,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  ListSubheader,
  Collapse,
} from "@mui/material";

//styles and theme
import { useTheme } from "@mui/material/styles";
import { BrowserView, MobileView } from "react-device-detect";
import PerfectScrollbar from "react-perfect-scrollbar";

//icons
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useLocation, useNavigate } from "react-router-dom";
//store
import { useDispatch, useSelector } from "react-redux";
import { setDrawer } from "../../../app/slices/appSlice";
import SidebarLinks from "../../../components/SidebarLinks.js";
import { useHasPermission } from "../../../hooks/useHasPermission";

const SideBar = () => {
  const {
    user: {
      info: { has_team },
    },
  } = useSelector((state) => state.profile);
  const { hasPermission, roleName, isAdmin } = useHasPermission();

  //uses
  const theme = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  //constants
  const drawerWidth = 220;

  //states
  const [open, setOpen] = useState(false);
  const [selectedID, setSelectedID] = useState(null);

  //selectors and media queries
  const drawerOpen = useSelector((state) => state.app.is_drawer_opened);
  const matchUpMd = useMediaQuery(theme.breakpoints.up("md"));

  const isLinkAllowed = (link) => {
    if (link.allowAll) return true;

    if (isAdmin) {
      return (
        hasPermission(null, link.permissionName) ||
        (link.has_team_required && has_team)
      );
    }

    if (link.has_team_required) {
      return has_team && link.roles?.includes(roleName);
    }

    return link.roles?.includes(roleName);
  };

  const drawerSubLinks = (link, index) => {
    return link.sub && link.sub.length > 0 ? (
      <Collapse in={open && index === selectedID} timeout="auto" unmountOnExit>
        <List dense={true} component="div" disablePadding>
          {link.sub.map((item, subIndex) => (
            <ListItemButton
              key={subIndex}
              sx={{ pl: 4 }}
              onClick={() => {
                navigate(item.path);
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: "30px",
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.name} />
            </ListItemButton>
          ))}
        </List>
      </Collapse>
    ) : null;
  };

  const allowedLinks = SidebarLinks.filter(isLinkAllowed);
  let currentGroup = "";

  const drawerLinks = (
    <Box m={1}>
      <List
        dense={false}
        sx={{
          "& .MuiListItem-root:hover": {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.main,
            borderRadius: "8px",
          },
          "& .MuiListItemButton-root:hover": {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.main,
            borderRadius: "8px",
          },
          "& .MuiListItemButton-root:hover .MuiListItemIcon-root": {
            color: theme.palette.primary.main,
          },
          "& .Mui-selected": {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.main,
            borderRadius: "8px",
            fontWeight: "bold",
          },
          "& .Mui-selected .MuiListItemIcon-root": {
            color: theme.palette.primary.main,
          },
        }}
      >
        {allowedLinks.map((item, index) => {
          let renderTitle = null;
          if (item.groupTitle && item.groupTitle !== currentGroup) {
            const isFirstGroup = currentGroup === "";
            currentGroup = item.groupTitle;
            renderTitle = (
              <React.Fragment key={`group-${item.groupTitle}`}>
                {!isFirstGroup && <Divider sx={{ my: 1, opacity: 0.6 }} />}
                <ListSubheader
                  disableSticky
                  sx={{
                    backgroundColor: "transparent",
                    lineHeight: "30px",
                    fontWeight: "bold",
                    fontSize: "0.85rem",
                    color: theme.palette.primary.dark,
                    mt: 1,
                    mb: 0.5,
                  }}
                >
                  {item.groupTitle}
                </ListSubheader>
              </React.Fragment>
            );
          }

          const hasSub = item.sub && item.sub.length > 0;
          const isMenuOpen = selectedID === index && open;

          return (
            <React.Fragment key={item.id || index}>
              {renderTitle}
              <ListItem disablePadding>
                <ListItemButton
                  selected={item.path === "/" + pathname.split("/")[1]}
                  onClick={() => {
                    if (!hasSub) navigate(item.path);
                    if (index === selectedID) {
                      setOpen(!open);
                    } else {
                      setOpen(true);
                      setSelectedID(index);
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: "25px",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      fontSize: "0.8rem",
                    }}
                    disableTypography={true}
                    primary={item.name}
                  />
                  {hasSub ? (
                    isMenuOpen ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )
                  ) : null}
                </ListItemButton>
              </ListItem>
              {drawerSubLinks(item, index)}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );

  const drawerViewAndScroll = (
    <>
      <Box>
        <Box sx={{ display: "flex", mx: "auto", justifyContent: "center" }}>
          <img
            src={require("../../../assets/images/hr.png")}
            height={65}
            alt="logo"
          />
        </Box>
      </Box>
      <BrowserView>
        <PerfectScrollbar component="div">
          {drawerLinks}
        </PerfectScrollbar>
      </BrowserView>
      <MobileView>
        <Box sx={{ px: 2 }}>{drawerLinks}</Box>
      </MobileView>
    </>
  );

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { md: 0 }, width: drawerOpen ? drawerWidth : "0px" }}
      aria-label="sidebar"
    >
      <Drawer
        dir="rtl"
        variant={matchUpMd ? "persistent" : "temporary"}
        anchor="left"
        open={drawerOpen}
        onClose={() => dispatch(setDrawer())}
        sx={{
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            borderRight: "none",
          },
        }}
        ModalProps={{ keepMounted: true }}
        color="inherit"
      >
        {drawerViewAndScroll}
      </Drawer>
    </Box>
  );
};

export default SideBar;
